import { computed, ref } from 'vue';
import {
  checkPasswordReset as checkPasswordResetRequest,
  executePasswordReset as executePasswordResetRequest,
  fetchUser as fetchUserRequest,
  initiatePasswordReset as initiatePasswordResetRequest,
  login as loginRequest,
  loginSso as loginSsoRequest,
  verifyOtp as verifyOtpRequest,
  logout as logoutRequest,
  register as registerRequest,
  setUserTimezone,
} from '@/services/api/auth';
import { LoggedInUser, UserQuota } from '@/entities/auth/loggedInUser';
import { hasToken, setToken, unsetToken } from '@/services/token';
import { chain, fromIOEither, map } from 'fp-ts/lib/TaskEither';
import * as IOE from 'fp-ts/lib/IOEither';
import { flow, pipe } from 'fp-ts/lib/function';
import * as track from '@/services/telemetry';
import { ForgottenPasswordUser } from '@/entities/auth/forgottenPasswordUser';
import { Token } from '@/entities/auth/token';
import { ApiError } from '@/services/api';
import { Feature, Permissions, Quota } from './auth.types';
import { emptyUser } from '@/ui/auth/emptyUser';
import { User as SentryUser } from '@sentry/types/types/user';
import * as Sentry from '@sentry/browser';
import { TrialStatus } from '@/entities/organization/constants';
import { SubscriptionStatus } from '@/entities/subscription/constants';
import { SubscriptionStatus as CbSubscriptionStatus } from '@/entities/chargebee/subscription/constants';
import { userHasFreePlan, userPaysByInvoice } from '@/ui/helpers';
import {
  areApprovalsEnabled,
  features,
  isIntegrationsEnabled,
  isSsoEnabled,
  isFormTranslationsEnabled,
  isManualFormTranslationsEnabled,
  isAiPricingEnabled,
  isAiImageDescriptionEnabled,
} from '@/ui/helpers/featureHelper';
import { Userflow } from '@/services/userflow/userflow';
import emitter from '@/services/emitter';

// takes care of initial logged in state set
export const loggedIn = ref(hasToken());

// this is always false on page load
export const userFetched = ref(false);
export const user = ref<LoggedInUser>(emptyUser);
export const isOnFreePlan = computed(() => {
  if (!user.value.subscription) {
    return false;
  }

  return user.value.subscription.subscriptionItems.every(
    item => item.itemPriceId.includes('Free'),
  );
});
export const isInFreeTrial = computed(() => {
  return user.value.subscription?.status === CbSubscriptionStatus.InTrial
    || user.value.organization.trialStatus === TrialStatus.FREE_TRIAL_STATUS_CURRENT;
});
export const userHasPaymentMethod = computed(() => user.value.organization?.hasPaymentMethod);
export const chargebeeSubscription = computed(() => user.value.subscription);
export const showBuyNowButton = computed(() => {
  if (userPaysByInvoice()) {
    return false;
  }

  return isInFreeTrial.value && !userHasPaymentMethod.value;
});

export const justFinishedFreeTrial = computed(() => {
  return user.value.organization.trialStatus === TrialStatus.FREE_TRIAL_STATUS_JUST_FINISHED;
});
export const canSelfServeSubscription = computed(() => {
  return user.value.organization.canSelfServeSubscription;
});
export const hasConvertedForm = computed(() => user.value.organization.formConverted);
export const approvalsEnabledForOrganization = computed(
  () => areApprovalsEnabled() && user.value.organization.addons.approvalsEnabled,
);

export const isFormTranslationsFeatureEnabled = computed(
  () => isFormTranslationsEnabled() && user.value.organization.addons.formTranslationsEnabled,
);

export const isManualFormTranslationsFeatureEnabled = computed(
  () => isManualFormTranslationsEnabled() && user.value.organization.addons.manualFormTranslationsEnabled,
);

export const isAiImageDescriptionFeatureEnabled = computed(
  () => isAiImageDescriptionEnabled() && user.value.organization.addons.aiImageDescriptionEnabled,
);

export const hasAiBalance = computed(() => isAiPricingFeatureEnabled.value ? user.value?.aiWallet?.available > 0 : true);

export const isAiPricingFeatureEnabled = computed(() => isAiPricingEnabled());

export const isTrainingsModuleEnabled = computed(
  () => user.value.organization.addons.trainingsModuleEnabled,
);

const setTimezone = (loggedInUser: LoggedInUser) => {
  const u: LoggedInUser = { ...loggedInUser };

  if (!u.timezone) {
    u.timezone = 'Europe/Berlin';
    try {
      u.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(u.timezone)();
    } catch (e) {
      console.error(e);
    }
  }

  return u;
};

export const fetchUser = () => pipe(
  fetchUserRequest(),
  map(setTimezone),
  map(fetchedUser => {
    if (loggedIn.value) {
      user.value = fetchedUser;
      userFetched.value = true;

      const sentryUser: SentryUser = {
        email: user.value.email,
      };

      features.value = user.value.features;
      Sentry.setUser(sentryUser);

      if (Userflow.isUserflowEnabled()) {
        Userflow.identify(user.value);
      }
    }
    return fetchedUser;
  }),
);

const setLoginState = ({ token }: Token) => pipe(
  setToken(token),
  IOE.map(() => {
    loggedIn.value = true;
    emitter.emit('sessionLimitReachedReset');
    return undefined;
  }),
  IOE.mapLeft(() => new ApiError(0, 'Can not store token, local storage quota might be full.')),
  fromIOEither,
);

export const login = flow(
  loginRequest,
  chain(setLoginState),
  chain(fetchUser),
  map(loggedInUser => {
    track.login(loggedInUser);
    return loggedInUser;
  }),
);

export const loginSso = flow(
  loginSsoRequest,
);

export const verifyOtp = flow(
  verifyOtpRequest,
);

export const resetLogin = () => {
  unsetToken();
  loggedIn.value = false;
  userFetched.value = false;
  user.value = emptyUser;
};

export const logout = () => pipe(
  logoutRequest(),
  map(response => {
    resetLogin();
    track.logout(user.value);
    return response;
  }),
);

export const impersonate = (token: string) => flow(
  resetLogin,
  () => setLoginState({
    token,
    tokenType: 'jwt',
  }),
  chain(fetchUser),
);

export const loginByToken = (token: string) => flow(
  resetLogin,
  () => setLoginState({
    token,
    tokenType: 'jwt',
  }),
  chain(fetchUser),
  map(loggedInUser => {
    track.login(loggedInUser);

    return loggedInUser;
  }),
);

export const register = flow(
  registerRequest,
  chain(setLoginState),
  chain(fetchUser),
  map(loggedInUser => {
    track.login(loggedInUser);
    track.register(loggedInUser);
    return loggedInUser;
  }),
);

export const initiatePasswordReset = (forgottenPasswordUser: ForgottenPasswordUser) => pipe(
  forgottenPasswordUser,
  initiatePasswordResetRequest,
  map(() => {
    track.passwordResetInitiated(forgottenPasswordUser);
  }),
);

export const executePasswordReset = flow(
  executePasswordResetRequest,
  chain(setLoginState),
  chain(fetchUser),
  map(loggedInUser => {
    track.login(loggedInUser);
    track.passwordReset(loggedInUser);
  }),
);

export const executePasswordSet = flow(
  executePasswordResetRequest,
  chain(setLoginState),
  chain(fetchUser),
  map(loggedInUser => {
    track.login(loggedInUser);
  }),
);

export const checkPasswordReset = flow(
  checkPasswordResetRequest,
);

export function hasFeature(feature: Feature): boolean {
  if (!user.value.subscriptionFeatures) {
    return false;
  }
  return user.value.subscriptionFeatures.includes(feature);
}

export function hasPermission(permission: Permissions): boolean {
  return user.value.admin || user.value.permissions.includes(permission);
}

export function hasSomePermission(permissions: Permissions[]): boolean {
  return permissions.some(permission => hasPermission(permission));
}

export function isSubscriptionPastDue() {
  return user.value.organization.subscription?.status === SubscriptionStatus.PastDue
    || user.value.subscription?.isPastDue;
}

export function organizationHasAddedPaymentMethod() {
  return user.value.organization.subscription?.hasPaymentMethod
    || user.value.organization?.hasPaymentMethod;
}

export function hasSubscription(): boolean {
  return user.value.subscription !== null && user.value.subscription.status !== CbSubscriptionStatus.Cancelled;
}

export function canOpenSubscriptionPage(): boolean {
  return hasPermission(Permissions.MANAGE_BILLING)
    && !userHasFreePlan(user.value)
    //organizations in Free Trial can open Subscriptions page only when they have "can self serve" flag (SL-5627)
    && (!isInFreeTrial.value || canSelfServeSubscription.value);
}

/**
 * Indicates if there is a higher plan to upgrade to.
 * The extendUserQuota is an arbitrary feature that is not present
 * in the free plan, so if a user does not have it, it can upgrade.
 */
export function canUpgrade(): boolean {
  return !hasFeature('extendUserQuota');
}

export const calculateQuotaRate = (userQuota: UserQuota) => (userQuota.current / userQuota.max) * 100;

export function findQuota(quota: Quota): UserQuota | undefined {
  if (!user.value.subscriptionQuotas.length) {
    return undefined;
  }
  return user.value.subscriptionQuotas.find(
    ({ entity }) => entity === quota,
  );
}

// This is only sent by the backend when the user doesn't have a subscription
// e.g. { subscription: null }
// todo: fetch from api always
export function getOrganizationUsersCount() {
  return user.value.organization.usersCount
    ? user.value.organization.usersCount
    : findQuota('user')?.current ?? 1;
}

export const appliedForDiscount = () => user.value.organization.appliedForDiscount;

export function hasQuota(quota: Quota): boolean {
  return findQuota(quota) !== undefined;
}

export function getQuota(quota: Quota): number {
  const q = findQuota(quota);

  if (q === undefined || q === null) {
    return -1;
  }

  if (q.max === null) {
    return -1;
  }

  return Math.max(q.max - q.current, 0);
}

export function isOrganizationAdministrator(): boolean {
  return user.value.admin;
}

export function hasPublicApiEnabled(): boolean {
  return isOrganizationAdministrator() && user.value.organization.addons.publicApiEnabled;
}

export const hasSsoEnabled = computed(() => {
  return isOrganizationAdministrator() && isSsoEnabled() && user.value.organization.addons.ssoEnabled;
});

export const hasIntegrationsEnabled = computed(() => {
  return hasPermission(Permissions.MANAGE_INTEGRATIONS) && isIntegrationsEnabled();
});

export const hasInspectionQuotaLeft = computed<boolean>(
  () => hasQuota('inspection') ? getQuota('inspection') > 0 : true,
);

export const hasCollapsibleTemplateBuilder = computed(() => {
  const organizations = (process.env.VUE_APP_COLLAPSIBLE_ORGANIZATIONS || '').split(',').filter(_ => _).map(Number);

  return organizations.includes(user.value.organization.id);
});

export const isOrganizationCreatedAfterAnalyticsV2 = computed(() => {
  const baseDate = process.env.VUE_APP_ANALYTICS_V2_RELEASE_DATE;

  if (!baseDate) {
    return false;
  }

  return user.value.organization.createdAt > new Date(baseDate);
});

export const currentUserQuota = computed<number>(() => getQuota('user'));

export const currentUsersCount = computed<number>(
  () => {
    return findQuota('user')?.current ?? 1;
  },
);

export function canAddUser(): boolean {
  return isOnFreePlan.value || currentUserQuota.value > 0 || isInFreeTrial.value;
}

// this interval updates the data of the currently logged in user every two minutes.
// this way, all reactive values that depend on this data will update if the data changes.
setInterval(() => {
  if (hasToken()) {
    // this looks strange but this is just a way to dispatch the lazy promise;
    fetchUser()();
  }
}, 2 * 60 * 1000); // every two minutes

export * from './auth.types';
